'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ChevronLeft, ArrowUpRight } from 'lucide-react';
import { apiGetAllNews } from '@/services/newsHome/api';

interface Project {
    id: string;
    title: string;
    description: string;
    image: string;
    detailsLink: string;
}

const ITEMS_PER_PAGE = 3;

interface FeaturedProjectsProps {
    companyId?: string | number;
}

export default function FeaturedProjects({ companyId }: FeaturedProjectsProps) {
    const params = useParams();

    const finalId = companyId ? String(companyId) : (params.id as string);

    const { data: newsData, isLoading } = useQuery({
        queryKey: ['projects', finalId],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: Number(finalId),
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        enabled: !!finalId && !isNaN(Number(finalId)),
        staleTime: 5 * 60 * 1000,
    });

    const [currentPage, setCurrentPage] = useState(1);

    // --- 1. Hàm tạo Slug chuẩn ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const allProjects: Project[] = (newsData && Array.isArray(newsData))
        ? newsData
            .filter((item: any) => item.seoDescription === 'Dự án')
            .map((item: any) => {
                return {
                    id: item.id.toString(),
                    title: item.title,
                    description: item.description,
                    image: item.image || '',
                    detailsLink: item.url || '#',
                };
            })
        : [];

    if (!isLoading && allProjects.length === 0) {
        return null;
    }

    if (isLoading) return null;

    const featuredProject = allProjects[0];
    const otherProjects = allProjects.slice(1);

    const totalPages = Math.max(1, Math.ceil(otherProjects.length / ITEMS_PER_PAGE));
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedProjects = otherProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    const displayProjects = paginatedProjects;

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <section className="w-full bg-white pt-10 pb-2" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            <div className="mx-auto max-w-[62rem] px-4">
                <h2 className="mb-8 text-center text-3xl font-medium text-[#2D3746]">
                    Các dự án tiêu biểu
                </h2>

                {featuredProject && (() => {
                    const slugTitle = createSlug(featuredProject.title);
                    const projectHref = `/news/${slugTitle}-${featuredProject.id}`;

                    return (
                        <Link
                            href={projectHref}
                            className="mb-8 lg:mb-12 flex flex-col lg:flex-row overflow-hidden rounded-2xl lg:rounded-3xl shadow-sm h-auto lg:h-[350px] group cursor-pointer transition-shadow hover:shadow-md"
                        >
                            <div className="relative w-full lg:w-[65%] h-56 sm:h-72 lg:h-full flex-shrink-0">
                                <Image
                                    src={featuredProject.image || '/placeholder-project.jpg'}
                                    alt={featuredProject.title}
                                    fill
                                    className="object-cover"
                                />
                            </div>
                            <div
                                className="relative w-full lg:w-[35%] p-6 lg:p-10 flex flex-col text-white"
                                style={{
                                    backgroundImage: "url('/images/Investment/BG.png')",
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat'
                                }} >

                                <div className="relative z-10 flex flex-col h-full justify-start pt-0 lg:pt-2 text-[#FFFFFF]">
                                    <h3 className="mb-2 lg:mb-1.5 text-xl lg:text-2xl font-bold leading-tight uppercase tracking-tight line-clamp-2">
                                        {featuredProject.title}
                                    </h3>

                                    <p className="mb-4 text-xs leading-normal text-white/80 line-clamp-4 font-light whitespace-pre-line">
                                        {featuredProject.description}
                                    </p>

                                    <div className="mt-auto lg:mt-0">
                                        <span
                                            className="inline-flex items-center gap-2 text-xs font-thin hover:text-white/80 transition-colors underline underline-offset-4 decoration-0"
                                        >
                                            Xem chi tiết
                                            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    )
                })()}

                {displayProjects.length > 0 && (
                    <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
                        {displayProjects.map((project) => {
                            const slugTitle = createSlug(project.title);
                            const projectHref = `/news/${slugTitle}-${project.id}`;

                            return (
                                <Link
                                    key={project.id}
                                    href={projectHref}
                                    className="flex flex-col group cursor-pointer"
                                >
                                    <div className="relative h-48 w-full overflow-hidden rounded-xl mb-4">
                                        <Image
                                            src={project.image || '/placeholder-project.jpg'}
                                            alt={project.title}
                                            fill
                                            className="object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="text-base font-bold text-[#58595B] mb-1 line-clamp-2 uppercase group-hover:text-[#006633] transition-colors">
                                            {project.title}
                                        </h3>
                                        <p className="text-xs text-[#58595B] mb-3 line-clamp-3 flex-grow whitespace-pre-line">
                                            {project.description}
                                        </p>
                                        <span
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#006633] transition-colors underline underline-offset-4 mt-auto"
                                        >
                                            Xem chi tiết
                                            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                                        </span>
                                    </div>
                                </Link>
                            )
                        })}
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-1.5 mt-4">
                        <button
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="group flex h-6 w-6 items-center justify-center rounded-full border border-black bg-white text-black transition-all hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-300"
                        >
                            <ChevronLeft className="h-2.5 w-2.5" strokeWidth={2} />
                        </button>

                        <div className="flex items-center gap-0.5">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => goToPage(page)}
                                    className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold transition-all
                                    ${page === currentPage
                                            ? 'bg-[#006633] text-white'
                                            : 'bg-transparent text-gray-500 hover:text-[#006633]'
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="group flex h-6 w-6 items-center justify-center rounded-full border border-black bg-white text-black transition-all hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-300"
                        >
                            <ChevronRight className="h-2.5 w-2.5" strokeWidth={2} />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}